import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { LocalizedComponentProps } from 'bernie-l10n';

import { DialogContentModel, DialogContentSplitViewModel } from 'models/common';

import { BarRatings } from 'components/infosite/property-details/bar-ratings';
import { FindAnswersInput } from 'components/infosite/find-answers/find-answers-input';
import { RatingWithSuperlative } from 'components/common/reviews/review-components';
import { ReviewsDisclaimerTrigger } from 'components/infosite/property-details/reviews-disclaimer-trigger';
import { ReviewsFilter } from 'components/infosite/property-details/reviews-filter';
import { ReviewsListOnDialog } from 'components/infosite/property-details/reviews-list-on-dialog';
import { ReviewsSortTabs } from 'components/infosite/property-details/reviews-sort-tabs';

export const DialogContentSplitView = ({
  reviews,
  questionAnswersViewModel,
  l10n,
  onMoreReviews,
  applyRatingsFilters,
  applyTravelerTypeFilters,
  applyLanguageTypeFilters,
  ratingFiltersOptions,
  travelerTypeFilterOptions,
  languageFilterOptions,
  isDateless
}: DialogContentSplitViewModel) => {
  const { superlative, count, localizedCount, localizedCountWithLabel, localizedOverallRating, reviewDisclaimer } = reviews;
  const onMoreRecentReviews = () => onMoreReviews(true);
  const countMessage = count <= 1 ? 'hotels.infosite.reviews.verifiedReview.text.single' : 'hotels.infosite.reviews.verifiedReview.text';
  const verifiedReviewCountMessage = l10n.formatText(countMessage, localizedCount);

  return (
    <div className="uitk-grid l-x-padding-twelve xl-x-padding-twelve l-t-padding-four xl-t-padding-four">
      <div className="uitk-cell all-cell-1-3 all-r-padding-three l-l-padding-six xl-l-padding-twelve">
        {localizedOverallRating && <RatingWithSuperlative rating={localizedOverallRating} superlative={superlative} />}
        <ReviewsCount
          count={count}
          localizedCount={localizedCount}
          localizedCountWithLabel={localizedCountWithLabel}
          verifiedReviewCountMessage={verifiedReviewCountMessage}
          reviewDisclaimer={reviewDisclaimer}
        />
        <BarRatings
          className="uitk-grid all-cell-1-1 all-x-gutter-four all-y-gutter-four l-b-padding-four xl-b-padding-four"
          barClassName="uitk-cell"
          reviews={reviews}
          l10n={l10n}
        />
        {questionAnswersViewModel && <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} reviewEntry={true} />}
        <AbacusExperiment name="PWA_Social_Filter_Review_Score">
          <AbacusControl />
          <AbacusVariant bucket={1}>
            <ReviewsFilter
              applyReviewsFilter={applyRatingsFilters}
              checkboxOptions={ratingFiltersOptions}
              label={l10n.formatText('hotels.infosite.reviews.filterLabel.guestRatings')}
              isDateless={isDateless}
            />
          </AbacusVariant>
        </AbacusExperiment>
        <AbacusExperiment name="PWA_Reviews_traveler_type_filter">
          <AbacusControl />
          <AbacusVariant bucket={1}>
            <ReviewsFilter
              applyReviewsFilter={applyTravelerTypeFilters}
              checkboxOptions={travelerTypeFilterOptions}
              label={l10n.formatText('hotels.infosite.reviews.filterLabel.travelerType')}
              isDateless={isDateless}
            />
          </AbacusVariant>
        </AbacusExperiment>
        <ReviewsFilter
          applyReviewsFilter={applyLanguageTypeFilters}
          radioOptions={languageFilterOptions}
          label={languageFilterOptions[0].filterName}
          radioGroupName="LanguageFilterGroup"
          isDateless={isDateless}
        />
      </div>
      <div className="uitk-cell all-cell-fill all-l-padding-three l-r-padding-six xl-r-padding-twelve">
        <ReviewsListOnDialog onMoreReviews={onMoreRecentReviews} areTabsPresent={true} hideTopPadding={true} />
      </div>
    </div>
  );
};
DialogContentSplitView['displayName'] = 'DialogContentSplitView';

export const DialogContent = ({
  reviews,
  questionAnswersViewModel,
  l10n,
  onMoreReviews,
  reviewsListSectionTag,
  positiveReviews,
  criticalReviews,
  applyRatingsFilters,
  applyTravelerTypeFilters,
  applyLanguageTypeFilters,
  ratingFiltersOptions,
  travelerTypeFilterOptions,
  languageFilterOptions,
  ratingFilterCount,
  travelerFilterCount,
  languageFilterCount,
  showRatingFilterSheet,
  showTravelerFilterSheet,
  showLanguageFilterSheet,
  toggleRatingFilterSheet,
  toggleTravelerFilterSheet,
  toggleLanguageFilterSheet,
  reviewLanguageFilters,
  isDateless
}: DialogContentModel) => {
  const { superlative, count, localizedCount, localizedCountWithLabel, localizedOverallRating, reviewDisclaimer } = reviews;
  const onMoreRecentReviews = () => onMoreReviews(true);
  const countMessage = count <= 1 ? 'hotels.infosite.reviews.verifiedReview.text.single' : 'hotels.infosite.reviews.verifiedReview.text';
  const verifiedReviewCountMessage = l10n.formatText(countMessage, localizedCount);

  return (
    <>
      <section className="reviews-gutter-horz bar-ratings s-t-padding-two m-t-padding-four l-t-padding-four xl-t-padding-four">
        {localizedOverallRating && <RatingWithSuperlative rating={localizedOverallRating} superlative={superlative} />}
        <ReviewsCount
          count={count}
          localizedCount={localizedCount}
          localizedCountWithLabel={localizedCountWithLabel}
          verifiedReviewCountMessage={verifiedReviewCountMessage}
          reviewDisclaimer={reviewDisclaimer}
        />
        <BarRatings
          className="uitk-grid s-cell-1-1 all-cell-1-2 all-x-gutter-four all-y-gutter-four all-b-padding-four uitk-card-separator-bottom"
          barClassName="uitk-cell"
          reviews={reviews}
          l10n={l10n}
        />
      </section>
      <section className={reviewsListSectionTag} data-stid="reviews-list-section">
        {questionAnswersViewModel && <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} reviewEntry={true} />}
        <AbacusExperiment name="LSHOP_PWA_FILTER_GUEST_REVIEWS">
          <AbacusControl>
            <>
              <span className="uitk-flex uitk-flex-gap-two">
                <AbacusExperiment name="PWA_Social_Filter_Review_Score">
                  <AbacusControl />
                  <AbacusVariant bucket={1}>
                    <ReviewsFilter
                      applyReviewsFilter={applyRatingsFilters}
                      checkboxOptions={ratingFiltersOptions}
                      countSelected={ratingFilterCount}
                      label={l10n.formatText('hotels.infosite.reviews.filterLabel.guestRatings')}
                      showSheet={showRatingFilterSheet}
                      toggleSheet={toggleRatingFilterSheet}
                      isDateless={isDateless}
                    />
                  </AbacusVariant>
                </AbacusExperiment>
                <AbacusExperiment name="PWA_Reviews_traveler_type_filter">
                  <AbacusControl />
                  <AbacusVariant bucket={1}>
                    <ReviewsFilter
                      applyReviewsFilter={applyTravelerTypeFilters}
                      checkboxOptions={travelerTypeFilterOptions}
                      countSelected={travelerFilterCount}
                      label={l10n.formatText('hotels.infosite.reviews.filterLabel.travelerType')}
                      showSheet={showTravelerFilterSheet}
                      toggleSheet={toggleTravelerFilterSheet}
                      isDateless={isDateless}
                    />
                  </AbacusVariant>
                </AbacusExperiment>
                <ReviewsFilter
                  reviewLanguageFilters={reviewLanguageFilters}
                  applyReviewsFilter={applyLanguageTypeFilters}
                  radioOptions={languageFilterOptions}
                  countSelected={languageFilterCount}
                  label={languageFilterOptions && languageFilterOptions[0].filterName}
                  showSheet={showLanguageFilterSheet}
                  toggleSheet={toggleLanguageFilterSheet}
                  radioGroupName="LanguageFilterGroup"
                  isDateless={isDateless}
                />
              </span>
              <ReviewsListOnDialog onMoreReviews={onMoreRecentReviews} hideTopPadding={false} />
            </>
          </AbacusControl>
          <AbacusVariant bucket={1}>
            <ReviewsSortTabs
              reviews={reviews}
              positiveReviews={positiveReviews}
              criticalReviews={criticalReviews}
              onMoreReviews={onMoreReviews}
            />
          </AbacusVariant>
        </AbacusExperiment>
      </section>
    </>
  );
};
DialogContent['displayName'] = 'DialogContent';

export interface ReviewsCountProps extends LocalizedComponentProps {
  count?: number;
  localizedCount?: string;
  localizedCountWithLabel?: string;
  verifiedReviewCountMessage?: string;
  reviewDisclaimer?: string;
}

export const ReviewsCount: React.FunctionComponent<ReviewsCountProps> = ({
  count,
  localizedCount,
  localizedCountWithLabel,
  verifiedReviewCountMessage,
  reviewDisclaimer
}) =>
  count &&
  localizedCount && (
    <AbacusExperiment name="PWA_Review_Disclaimer">
      <AbacusControl>
        <div className="uitk-type-subheading pwa-theme--grey-800 all-t-padding-one all-b-padding-three">{localizedCountWithLabel}</div>
      </AbacusControl>
      <AbacusVariant bucket={1}>
        <ReviewsDisclaimerTrigger countMessage={verifiedReviewCountMessage} reviewDisclaimer={reviewDisclaimer} />
      </AbacusVariant>
    </AbacusExperiment>
  );
ReviewsCount['displayName'] = 'ReviewsCount';
